import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Eye, ArrowLeft, Share2, Tag } from 'lucide-react';
import { getBlogBySlug, incrementBlogViews, getBlogs } from '../firebase/collections';
import SEOHead from '../components/SEOHead';
import { formatTimestamp } from '../utils/timestampUtils';
import { generatePageStructuredData, generateBreadcrumbStructuredData } from '../utils/seoUtils';
import { generateMetaDescription } from '../utils/seoUtils';

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    loadBlog();
  }, [slug]);

  useEffect(() => {
    if (blog) {
      // Increment views
      incrementBlogViews(blog.id);
    }
  }, [blog]);

  const loadBlog = async () => {
    try {
      setLoading(true);
      const result = await getBlogBySlug(slug);
      if (result.success) {
        setBlog(result.data);
        // Load related blogs (same category)
        if (result.data.category) {
          const relatedResult = await getBlogs({
            status: 'published',
            category: result.data.category,
            limit: 3
          });
          if (relatedResult.success) {
            setRelatedBlogs(
              relatedResult.data.filter(b => b.id !== result.data.id).slice(0, 3)
            );
          }
        }
      }
    } catch (error) {
      console.error('Error loading blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blog.title,
        text: blog.excerpt,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Blog Post Not Found</h1>
          <Link to="/blog" className="text-blue-600 hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const metaDescription = blog.metaDescription || blog.excerpt || generateMetaDescription(blog.content);
  const metaTitle = blog.metaTitle || blog.title;
  const canonicalUrl = `https://mahstry.com/blog/${blog.slug || blog.id}`;
  const ogImage = blog.featuredImage || 'https://mahstry.com/og-image.jpg';

  // Generate structured data
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blog.title,
    "description": metaDescription,
    "image": ogImage,
    "datePublished": blog.createdAt?.toDate ? blog.createdAt.toDate().toISOString() : new Date(blog.createdAt).toISOString(),
    "dateModified": blog.updatedAt?.toDate ? blog.updatedAt.toDate().toISOString() : new Date(blog.updatedAt || blog.createdAt).toISOString(),
    "author": {
      "@type": "Person",
      "name": blog.authorName || "MahStry Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MahStry",
      "logo": {
        "@type": "ImageObject",
        "url": "https://mahstry.com/icon-512x512.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "keywords": blog.metaKeywords || blog.tags?.join(', ') || '',
    "articleSection": blog.category || "Education"
  };

  const breadcrumbStructuredData = generateBreadcrumbStructuredData([
    { name: 'Home', url: 'https://mahstry.com' },
    { name: 'Blog', url: 'https://mahstry.com/blog' },
    { name: blog.title, url: canonicalUrl }
  ]);

  const combinedStructuredData = {
    "@context": "https://schema.org",
    "@graph": [articleStructuredData, breadcrumbStructuredData]
  };

  return (
    <>
      <SEOHead
        title={`${metaTitle} | MahStry Blog`}
        description={metaDescription}
        keywords={blog.metaKeywords || blog.tags?.join(', ') || 'study tips, exam preparation, education'}
        canonical={canonicalUrl}
        ogImage={ogImage}
        structuredData={combinedStructuredData}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            {blog.category && (
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full mb-4">
                {blog.category}
              </span>
            )}
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{blog.title}</h1>
            <div className="flex items-center gap-6 text-sm text-gray-600 mb-4">
              {blog.createdAt && (
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatTimestamp(blog.createdAt)}
                </span>
              )}
              {blog.authorName && (
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {blog.authorName}
                </span>
              )}
              {blog.views && (
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  {blog.views} views
                </span>
              )}
              <button
                onClick={handleShare}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {blog.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Featured Image */}
        {blog.featuredImage && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <img
              src={blog.featuredImage}
              alt={blog.title}
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
          </div>
        )}

        {/* Content */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: blog.content }}
              style={{
                fontFamily: 'Inter, Arial, sans-serif',
                lineHeight: '1.8'
              }}
            />
          </div>
        </article>

        {/* Related Blogs */}
        {relatedBlogs.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="text-2xl font-bold mb-6">Related Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedBlogs.map((relatedBlog) => (
                <Link
                  key={relatedBlog.id}
                  to={`/blog/${relatedBlog.slug || relatedBlog.id}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden"
                >
                  {relatedBlog.featuredImage && (
                    <img
                      src={relatedBlog.featuredImage}
                      alt={relatedBlog.title}
                      className="w-full h-40 object-cover"
                    />
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
                      {relatedBlog.title}
                    </h3>
                    {relatedBlog.excerpt && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {relatedBlog.excerpt}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlogDetail;

